import { useState, useEffect } from 'react';
import { useParams, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Check,
  Calendar,
  Users,
  Bus,
  MapPin,
  Bed,
  ListChecks,
  Download,
  ArrowRight,
  UserPlus,
  MessageCircle,
  Youtube,
  Clock3,
  Building2,
  CreditCard,
  MapPinned,
  Phone,
  Navigation2,
} from 'lucide-react';
import { bookingAPI, contactAPI } from '../services/api';
import { openBookingInvoice } from '../utils/bookingInvoice';
import { formatBookingPackageDateLabel } from '../utils/bookingPackageDates.js';
import { useSiteSettings } from '../hooks/useContent';
import { openRazorpayBookingPayment } from '../utils/razorpayCheckout';
import {
  userCanPayRemainingOnline,
  userCanPayNowOnline,
  bookingEventClosedForPayment,
  bookingHasRecordedPayment
} from '../utils/bookingPayment.js';
import { formatStayOptionLabel } from '../utils/formatStayOptionLabel.js';
import { downloadBrochureFromUrl } from '../utils/downloadBrochureFromUrl.js';
import { bookingRouteRef } from '../utils/bookingRouteRef';
import { resolveIndexedBookingParticipants } from '../utils/customerDisplayName.js';
import { onlineTotalWithGatewayFee } from '../utils/paymentGateway.js';
import LoadingIndicator from '../components/LoadingIndicator';
import PayRemainingButton from '../components/PayRemainingButton.jsx';
import { trackBookingConversion } from '../utils/googleAnalytics.js';
import './BookingConfirmed.css';

/** Human-facing code from API, or a short ref from booking id if code is missing. */
function getBookingCodeDisplay(booking) {
  if (!booking) return '';
  const raw = booking.bookingCode ?? booking.booking_code;
  const trimmed = raw != null ? String(raw).trim() : '';
  if (trimmed) {
    return trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
  }
  const idStr = booking.id != null ? String(booking.id).trim() : '';
  if (!idStr) return '';
  if (idStr.includes('-') && idStr.length >= 20) {
    return `Ref ${idStr.slice(0, 8)}…${idStr.slice(-4)}`;
  }
  return `Ref ${idStr}`;
}

export default function BookingConfirmed() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { settings } = useSiteSettings();
  const [booking, setBooking] = useState(null);
  const [offices, setOffices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    const loadBooking = async () => {
      try {
        const res = await bookingAPI.getById(id);
        if (!cancelled) setBooking(res.data?.data);
      } catch {
        if (!cancelled) setBooking(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadBooking();
    return () => { cancelled = true; };
  }, [id]);

  useEffect(() => {
    if (!booking || !id) return;
    const code = bookingRouteRef(booking);
    if (!code) return;
    const normalizedRoute = String(id).trim().replace(/^#/, '');
    const normalizedCode = String(code).trim().replace(/^#/, '');
    if (normalizedCode === normalizedRoute) return;
    navigate(`/bookings/confirmed/${encodeURIComponent(code)}${location.search}`, { replace: true });
  }, [booking, id, navigate, location.search]);

  useEffect(() => {
    if (!booking) return;
    trackBookingConversion(booking);
  }, [booking?.id, booking?.bookingCode]);

  useEffect(() => {
    let cancelled = false;
    contactAPI
      .getOffices()
      .then((res) => {
        if (cancelled) return;
        const list = Array.isArray(res.data?.data) ? res.data.data : [];
        setOffices(list);
      })
      .catch(() => {
        if (!cancelled) setOffices([]);
      });
    return () => { cancelled = true; };
  }, []);

  const formatDate = (d) =>
    d
      ? new Date(d).toLocaleDateString('en-IN', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        })
      : '';
  const batch = booking?.batch;
  const trek = batch?.trek;
  const joinFromName =
    batch?.trek?.packages?.find((p) => p.id === booking?.joinFromPackageId)?.name || 'Dehradun';
  // Travel label should reflect the actual selected package name (e.g. Rajdhani, Sponsored)
  const travelLabel =
    batch?.trek?.packages?.find((p) => p.id === booking?.joinFromPackageId)?.name ||
    booking?.travelPackage ||
    '—';
  const stayOptionsList = Array.isArray(trek?.stayOptions) && trek.stayOptions.length > 0 ? trek.stayOptions : [];
  const stayOptionName = stayOptionsList.find((o) => String(o.id) === String(booking?.stayOption))?.name;
  const stayLabel = formatStayOptionLabel(stayOptionName, booking?.stayOption);
  const travelerLabel =
    Number(booking?.travelerCount) === 1 ? '1 Traveller' : `${booking?.travelerCount} Travellers`;
  const indexedParticipants = resolveIndexedBookingParticipants(booking);
  const bookingCodeDisplay = getBookingCodeDisplay(booking);
  const prepVideoUrl = batch?.prepVideoUrl || '';
  const whatsappUrl = batch?.whatsappLink || '';
  const checklistPdfUrl = String(settings?.booking_interactive_checklist_pdf_url || '').trim();
  const searchParams = new URLSearchParams(location.search);
  const hasPayment = bookingHasRecordedPayment(booking);
  const isOfficeFlow = !hasPayment && searchParams.get('payment') === 'office';
  const officeFallback = {
    id: 'default-office',
    officeName: 'Head Office',
    address: settings?.contact_address || '308, University Plaza, Vijay Cross Roads, Navrangpura, Ahmedabad, Gujarat 380009',
    timings: settings?.contact_hours || null,
    phonePrimary: settings?.contact_phone || null,
    phoneSecondary: null,
    mapLink: null
  };
  const officeCards = offices.length > 0 ? offices : [officeFallback];

  const formatInr = (v) => `₹${Number(v || 0).toLocaleString('en-IN')}`;
  const advanceTarget = Number(booking?.advanceAmount ?? 0);
  const paidSoFar = Number(booking?.amountPaidOnline ?? 0) + Number(booking?.amountPaidAtOffice ?? 0);
  const dueAdvance = Math.max(0, advanceTarget - paidSoFar);
  const onlinePayAdvance = onlineTotalWithGatewayFee(dueAdvance);
  const showRemainingPayment = !isOfficeFlow && userCanPayRemainingOnline(booking);

  const refreshBooking = async () => {
    if (!booking?.id) return;
    const refreshed = await bookingAPI.getById(booking.id);
    setBooking(refreshed.data?.data || booking);
  };

  const handlePayOnline = async () => {
    if (!booking?.id) return;
    try {
      await openRazorpayBookingPayment({
        booking,
        paymentFor: 'advance',
        onSuccess: refreshBooking
      });
      window.location.replace(
        `/bookings/confirmed/${encodeURIComponent(bookingRouteRef(booking))}${window.location.search}`
      );
    } catch (err) {
      window.alert(err?.response?.data?.message || err.message || 'Payment failed. Please try again.');
    }
  };

  const handleOpenChecklist = async () => {
    if (!checklistPdfUrl) {
      window.alert('Interactive checklist PDF is not configured yet.');
      return;
    }
    await downloadBrochureFromUrl(checklistPdfUrl, {
      name: (trek?.name ? `${trek.name} Checklist` : 'Interactive-Checklist')
    });
  };

  if (loading) {
    return (
      <div className="booking-confirmed-page">
        <div className="booking-confirmed-inner">
          <div className="booking-confirmed-loading">
            <LoadingIndicator label="Loading..." />
          </div>
        </div>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="booking-confirmed-page">
        <div className="booking-confirmed-inner">
          <p>Booking not found.</p>
          <Link to="/bookings">Go to My Bookings</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="booking-confirmed-page">
      <div className="booking-confirmed-inner">
        <div className="booking-confirmed-card">
          <div className="booking-confirmed-header">
            <div className={`booking-confirmed-icon-wrap ${isOfficeFlow ? 'pending' : ''}`}>
              {isOfficeFlow ? <Clock3 size={40} strokeWidth={3} /> : <Check size={40} strokeWidth={3} />}
            </div>
            <h1 className="booking-confirmed-title">{isOfficeFlow ? 'Booking In Progress' : 'Booking Confirmed!'}</h1>
            <p className="booking-confirmed-sub">
              {isOfficeFlow
                ? `Your booking for ${trek?.name || 'the trip'} is reserved. Complete your advance payment to confirm your seat.`
                : `Your adventure to ${trek?.name || 'the trip'} is all set. Get ready to explore!`}
            </p>
            <div className="booking-confirmed-id-pill">
              <span className="booking-confirmed-id-label">BOOKING ID:</span>
              <span className="booking-confirmed-id-value">{bookingCodeDisplay}</span>
            </div>
            <p className="booking-confirmed-id-hint">
              This is your registration reference—use it on receipts and when you contact us. Old bookmark links with a long id still work.
            </p>
          </div>

          {showRemainingPayment && (
            <div className="booking-progress-section booking-remaining-payment-section">
              <div className="booking-progress-top-row">
                <h3 className="booking-progress-heading">
                  <CreditCard size={18} />
                  Remaining balance
                </h3>
                <PayRemainingButton
                  booking={booking}
                  variant="confirmed"
                  onPaid={refreshBooking}
                />
              </div>
              <p className="booking-confirmed-id-hint">
                Complete your trip payment online. A 2% payment gateway charge applies to online payments.
              </p>
            </div>
          )}

          {isOfficeFlow && (
            <div className="booking-progress-section">
              <div className="booking-progress-top-row">
                <h3 className="booking-progress-heading">
                  <Building2 size={18} />
                  Pay at Office
                </h3>
                {userCanPayNowOnline(booking) && !bookingEventClosedForPayment(booking) && (
                  <button
                    type="button"
                    className="booking-confirmed-btn primary booking-progress-online-btn booking-progress-online-btn--gateway"
                    onClick={handlePayOnline}
                  >
                    <span className="booking-progress-online-btn-inner">
                      <span className="booking-progress-online-btn-line1">
                        <CreditCard size={18} aria-hidden />
                        <span>
                          Pay Online — <strong>{formatInr(onlinePayAdvance.payableTotal)}</strong>
                        </span>
                      </span>
                      <span className="booking-progress-online-btn-line2">
                        +2% gateway ({formatInr(onlinePayAdvance.gatewayCharge)})
                      </span>
                    </span>
                  </button>
                )}
              </div>

              <div className="booking-progress-offices-grid">
                {officeCards.map((office) => {
                  const officeName = office?.officeName || office?.city || 'Head Office';
                  const officePhone = [office?.phonePrimary, office?.phoneSecondary].filter(Boolean).join(' / ');
                  return (
                    <article key={office.id} className="booking-progress-office-card">
                      <h4 className="booking-progress-office-title">{officeName}</h4>
                      <div className="booking-progress-office-body">
                        <div className="booking-progress-office-row booking-progress-office-row--address">
                          <span className="booking-progress-office-icon" aria-hidden>
                            <MapPinned size={18} />
                          </span>
                          <p>{office?.address}</p>
                        </div>
                        {office?.timings && (
                          <div className="booking-progress-office-row">
                            <span className="booking-progress-office-icon" aria-hidden>
                              <Clock3 size={18} />
                            </span>
                            <p>{office.timings}</p>
                          </div>
                        )}
                        {officePhone && (
                          <div className="booking-progress-office-row">
                            <span className="booking-progress-office-icon" aria-hidden>
                              <Phone size={18} />
                            </span>
                            <p>{officePhone}</p>
                          </div>
                        )}
                      </div>
                      {office?.mapLink && (
                        <a
                          href={office.mapLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="booking-progress-office-cta"
                        >
                          <Navigation2 size={16} />
                          View on Map
                        </a>
                      )}
                    </article>
                  );
                })}
              </div>
            </div>
          )}

          <div className="booking-confirmed-trek-card">
            <div className="booking-confirmed-trek-img">
              <img
                src={
                  trek?.media?.[0]?.url ||
                  batch?.trek?.brochureUrl ||
                  'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=500'
                }
                alt={trek?.name}
              />
            </div>
            <div className="booking-confirmed-trek-info">
              <h3 className="booking-confirmed-trek-name">{trek?.name}</h3>
              <div className="booking-confirmed-trek-cols">
                <div className="booking-confirmed-trek-col">
                  <p>
                    <Calendar size={18} />
                    {formatBookingPackageDateLabel(booking, formatDate)}
                  </p>
                  <p>
                    <Users size={18} />
                    {travelerLabel}
                  </p>
                  <p>
                    <Bus size={18} />
                    Travel: {travelLabel}
                  </p>
                </div>
                <div className="booking-confirmed-trek-col">
                  <p>
                    <MapPin size={18} />
                    {joinFromName} Departure
                  </p>
                  <p>
                    <Bed size={18} />
                    Accommodation: {stayLabel}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {indexedParticipants.length > 0 ? (
            <div className="booking-confirmed-participants">
              <h4 className="booking-confirmed-participants-title">
                {indexedParticipants.length > 1 ? 'Participants' : 'Participant'}
              </h4>
              <ol className="booking-confirmed-participants-list">
                {indexedParticipants.map((row) => (
                  <li key={`${row.index}-${row.name}`}>
                    <span className="booking-confirmed-participants-index">{row.index}.</span>
                    <span className="booking-confirmed-participants-name">{row.name}</span>
                  </li>
                ))}
              </ol>
            </div>
          ) : null}

          <div className="booking-confirmed-actions">
            <button
              type="button"
              className="booking-confirmed-btn outline"
              onClick={handleOpenChecklist}
            >
              <ListChecks size={20} />
              Interactive Checklist
            </button>
            <button
              type="button"
              className="booking-confirmed-btn outline"
              onClick={() => openBookingInvoice(booking)}
            >
              <Download size={20} />
              Download Proforma Invoice
            </button>
            <Link
              to={`/bookings/${encodeURIComponent(bookingRouteRef(booking))}/add-ons`}
              className="booking-confirmed-btn outline"
            >
              <Download size={20} />
              Add Add-ons
            </Link>
            <Link to="/dashboard" className="booking-confirmed-btn primary">
              Go to Dashboard
              <ArrowRight size={20} />
            </Link>
          </div>

          <div className="booking-confirmed-next">
            <h3 className="booking-confirmed-next-title">What&apos;s Next?</h3>
            <div
              className={`booking-confirmed-next-grid${isOfficeFlow ? ' booking-confirmed-next-grid--two' : ''}`}
            >
              <a
                href={prepVideoUrl || '#'}
                className="booking-confirmed-next-card"
                target={prepVideoUrl ? '_blank' : undefined}
                rel={prepVideoUrl ? 'noopener noreferrer' : undefined}
              >
                <div className="booking-next-icon-wrap youtube">
                  <Youtube size={28} />
                </div>
                <h4>Watch Prep Video</h4>
                <p>
                  Checkout our YouTube video on all you need to know about {trek?.name || 'this trek'}.
                </p>
              </a>
              <Link to="/profile" className="booking-confirmed-next-card">
                <div className="booking-next-icon-wrap profile">
                  <UserPlus size={28} />
                </div>
                <h4>Complete Your Profile</h4>
                <p>Add medical details and emergency contacts for all travellers.</p>
              </Link>
              {!isOfficeFlow && (
                <a
                  href={whatsappUrl || '#'}
                  className="booking-confirmed-next-card"
                  target={whatsappUrl ? '_blank' : undefined}
                  rel={whatsappUrl ? 'noopener noreferrer' : undefined}
                >
                  <div className="booking-next-icon-wrap whatsapp">
                    <MessageCircle size={28} />
                  </div>
                  <h4>Join WhatsApp Group</h4>
                  <p>Connect with your trek leader and fellow trekkers.</p>
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
